// frontend/admin-portal/src/components/products/BulkDeleteModal.jsx
import React, { useState } from "react";
import { Modal, Button, Alert, Badge } from "@fiberhome/shared-ui";
import { productService } from "../../services/productService";

export default function BulkDeleteModal({
  open,
  products = [],
  onClose,
  onDeleted,
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const handleDelete = async () => {
    setBusy(true);
    setError("");
    try {
      const ids = products.map((p) => p.id);
      const summary = await productService.bulkDelete(ids);
      setResult(summary);
      onDeleted?.(summary);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const handleClose = () => {
    setResult(null);
    setError("");
    onClose?.();
  };

  const count = products.length;

  return (
    <Modal
      open={open}
      onClose={busy ? undefined : handleClose}
      title={
        result
          ? "Delete Complete"
          : `Delete ${count} product${count === 1 ? "" : "s"}?`
      }
      size="lg"
      footer={
        result ? (
          <Button variant="primary" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={handleClose} disabled={busy}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDelete} loading={busy}>
              Delete {count} product{count === 1 ? "" : "s"}
            </Button>
          </>
        )
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      {/* Confirmation state */}
      {!result && (
        <>
          <Alert variant="warning" title="This cannot be undone">
            The selected products and their image references will be permanently
            removed.
          </Alert>

          <div className="fh-bulk-list">
            {products.slice(0, 30).map((p) => (
              <div key={p.id} className="fh-bulk-list__item">
                <div>
                  <div className="fh-bulk-list__name">{p.name}</div>
                  <div className="fh-bulk-list__sku">{p.sku}</div>
                </div>
                {p.category?.name && (
                  <Badge variant="default">{p.category.name}</Badge>
                )}
              </div>
            ))}
            {products.length > 30 && (
              <div className="fh-bulk-list__more">
                …and {products.length - 30} more
              </div>
            )}
          </div>
        </>
      )}

      {/* Result state */}
      {result && (
        <>
          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <div className="fh-import-stat">
                <div
                  className="fh-import-stat__value"
                  style={{ color: "var(--fh-success)" }}
                >
                  {result.deleted}
                </div>
                <div className="fh-import-stat__label">Deleted</div>
              </div>
            </div>
            <div className="col-md-6">
              <div className="fh-import-stat">
                <div
                  className="fh-import-stat__value"
                  style={{ color: "var(--fh-danger)" }}
                >
                  {result.failed}
                </div>
                <div className="fh-import-stat__label">Failed</div>
              </div>
            </div>
          </div>

          {result.failed > 0 && (
            <div className="fh-bulk-list">
              {result.items
                .filter((it) => it.status !== "deleted")
                .map((it) => (
                  <div key={it.id} className="fh-bulk-list__item">
                    <div>
                      <div className="fh-bulk-list__name">
                        {it.name || `Product #${it.id}`}
                      </div>
                      <div className="fh-bulk-list__sku">
                        {it.message || it.status}
                      </div>
                    </div>
                    <Badge variant="danger">failed</Badge>
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
